<template>
    <div>
        <section class="contentBoxTitle is-relative">
            <div class="container is-fluid py-0 px-4">
                <div class="column is-12 is-flex-direction-row">
                    <div
                        class="
                            is-inline-flex is-align-items-center
                            has-text-primary
                        "
                    >
                        <h6 class="is-subtitle-size is-primary mr-5">
                            {{ roomName }}
                        </h6>
                        <Icon family="fal" name="fa-file-download" />
                    </div>
                </div>
            </div>
        </section>
        <div class="is-justify-content-center box-center-vertical">
            <main class="main-content">
                <div class="box finish-call-container">
                    <div class="columns">
                        <div class="column is-6">
                            <div class="field">
                                <label class="label label-blue">
                                    {{ $t("call-details.call-information") }}
                                </label>
                                <p class="mb-2">
                                    <span class="has-text-weight-bold">{{
                                        $t("call-details.duration")
                                    }}</span>
                                    {{ duration }}
                                </p>
                                <p class="mb-2">
                                    <span class="has-text-weight-bold">{{
                                        $t("call-details.reason")
                                    }}</span>
                                    {{ roomName }}
                                </p>
                                <p class="mb-2">
                                    <span class="has-text-weight-bold">{{
                                        $t("call-details.description")
                                    }}</span>
                                    {{ roomDescription }}
                                </p>
                            </div>
                        </div>
                        <div class="column is-4">
                            <Participants :participants="participants" />
                        </div>
                        <div class="column is-2">
                            <QR
                                v-if="url !== ''"
                                :text="url"
                                :size="256"
                                error-level="L"
                            />
                        </div>
                    </div>
                    <Tabs>
                        <Tab
                            :name="$t('call-details.whiteboard')"
                            :selected="true"
                        >
                            <DataViewer
                                :getPagedData="getActions"
                                :notifyError="notifyError"
                                :fields="actionsFields"
                                :onRowSelect="evaluateAction"
                                :allowSearch="false"
                            />
                        </Tab>
                        <Tab v-if="isLogged" :name="$t('call-details.notes')">
                            <Notes :roomId="roomId" />
                        </Tab>
                        <Tab :name="$t('call-details.shared-files')">
                            <SharedFiles :roomId="roomId" />
                        </Tab>
                        <Tab :name="$t('call-details.messages')">
                            <Messages :roomId="roomId" />
                        </Tab>
                        <Tab v-if="isLogged" :name="$t('call-details.videos')">
                            <VideoEvidence :roomId="roomId" :roomName="roomName" />
                        </Tab>
                    </Tabs>
                </div>
            </main>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import QR from "vue-qr-generator";

import Participants from "@/components/conference-info/Participants.vue";
import ActionResult from "@/components/conference-info/ActionResult.vue";
import Notes from "@/components/conference/notes/Notes.vue";
import DataViewer from "@/components/datatable/DataViewer.vue";
import Tabs from "@/components/external-components/tabs/ShowTabs.vue";
import Tab from "@/components/external-components/tabs/ShowTab.vue";
import SharedFiles from "./SharedFiles";
import Messages from "./Messages";
import VideoEvidence from "./VideoEvidence";

import { RepositoryFactory } from "@/services/api";
import { mapGetters } from "vuex";

export default {
    name: "room-info",
    components: {
        DataViewer,
        Participants,
        QR,
        Tabs,
        Tab,
        Notes,
        SharedFiles,
        Messages,
        VideoEvidence,
    },
    props: {
        roomId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            roomInfo: null,
            participants: [],
            url: "",
            actionsFields: [
                {
                    ref: "sendDate",
                    displayText: "Hora",
                    cellClass: "is-vcentered",
                    render: (value) => {
                        return moment(value).locale("es").format("hh:mm:ss");
                    },
                },
                {
                    ref: "title",
                    displayText: "Evidencia",
                    cellClass: "is-vcentered",
                },
                {
                    ref: "receiver.name",
                    displayText: "Usuario",
                    cellClass: "is-vcentered",
                },
                {
                    ref: "actionType",
                    displayText: "Tipo de Acción",
                    cellClass: "is-vcentered",
                    render: (value) => {
                        switch (value) {
                            case 0:
                                return "Texto";
                            case 1:
                                return "Fichero";
                            case 2:
                                return "Foto";
                            case 3:
                                return "Fecha";
                            case 4:
                                return "Firma";
                            case 5:
                                return "Smartform";
                        }
                        return "";
                    },
                },
                {
                    displayText: "Respuesta",
                    component: ActionResult,
                },
            ],
        };
    },
    mounted() {
        this.url = window.location.href;
        this.getRoomInfo();
        this.getParticipants();
    },
    computed: {
        ...mapGetters("oidcStore", ["oidcIsAuthenticated", "oidcUser"]),
        isLogged() {
            return this.oidcIsAuthenticated;
        },
        roomName() {
            return this.roomInfo === null ? "" : this.roomInfo.name;
        },
        roomDescription() {
            return this.roomInfo === null ? "" : this.roomInfo.description;
        },
        duration() {
            let min = this.roomInfo === null ? "" : this.roomInfo.duration;
            return `${min} minutos`;
        },
    },
    methods: {
        async getRoomInfo() {
            try {
                var response = await RepositoryFactory.conference.getInfo({
                    roomId: this.roomId,
                });
                this.roomInfo = response.data;
            } catch (error) {
                window.console.log(error);
                window.console.log(error.message);
            }
        },
        async getParticipants() {
            try {
                var response =
                    await RepositoryFactory.conference.getParticipants({
                        roomId: this.roomId,
                    });
                this.participants = response.data;
            } catch (error) {
                window.console.log(error);
                window.console.log(error.message);
            }
        },
        async getActions() {
            try {
                var response = await RepositoryFactory.conference.getActions({
                    roomId: this.roomId,
                });
                return {
                    items: response.data,
                };
            } catch (error) {
                window.console.log(error);
                window.console.log(error.message);
            }
        },
        notifyError() {},
        evaluateAction(action) {
            window.console.log(action);
        },
    },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/_variables";
@import "src/assets/scss/_botones";
@import "src/assets/scss/_colores";

.contentBoxTitle {
    box-shadow: $box-shadow-h;
}

.box-center-vertical {
    position: relative;
    display: flex;
    flex-direction: row;
    flex: 1;
    min-height: calc(100vh - 7.5rem);
    background-color: $greyBG;
}

.finish-call-container {
    border-radius: 0px;
    width: 1100px;
    padding: 50px;
    height: 100%;
}

table.table thead th {
    border-bottom: 2px solid $primary;
}

table.table td {
    font-weight: 400;
}
</style>